import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

// Import POS service API
import { PosControllerService } from '@app/api/pos-service/api/posController.service';
import { PosResDTO } from '@app/api/pos-service/model/posResDTO';
import { PageInfoPosResDTO } from '@app/api/pos-service/model/pageInfoPosResDTO';
import { ShiftControllerService } from '@app/api/pos-service/api/shiftController.service';
import { ShiftsResDTO } from '@app/api/pos-service/model/shiftsResDTO';
import { PageInfoShiftsResDTO } from '@app/api/pos-service/model/pageInfoShiftsResDTO';
import { ShiftRequestDTO } from '@app/api/pos-service/model/shiftRequestDTO';
import { convertToInstant } from '@app/shared/utils/date-formatter.util';

// Import channel-service API for shop data
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { PhysicalShopShowDTO } from '@app/api/channel-service/model/physicalShopShowDTO';
import { PageInfoPhysicalShopVO } from '@app/api/channel-service/model/pageInfoPhysicalShopVO';
import { PhysicalShopDetailVO } from '@app/api/channel-service/model/physicalShopDetailVO';

// Local interfaces for POS Shift Opening Tracking
export interface PosShiftOpening {
  id: number;
  shift_id: string;
  posId: number;
  posCode: string;
  shopId: number;
  shopName: string;
  shopAddress: string;
  cashierId: number;
  cashierName: string;
  shiftDate: string;
  openingTime: string;
  closingTime?: string;
  openingAmount: number;
  closingAmount?: number;
  status: 'OPEN' | 'CLOSED' | 'CREATE';
  notes?: string;
  createdBy: string;
  creationDate: string;
  lastUpdatedBy?: string;
  lastUpdateDate?: string;
}

export interface PosShiftOpeningListResponse {
  data: PosShiftOpening[];
  totalRecords: number;
  loading: boolean;
}

export interface PosShiftOpeningSearchParams {
  posId?: number;
  cashierName?: string;
  startTimeFrom?: string;
  startTimeTo?: string;
  status?: number;
  page?: number;
  size?: number;
}

export interface ShopOption {
  label: string;
  value: number;
}

@Injectable({
  providedIn: 'root'
})
export class PosShiftOpenService {
  constructor(
    private posService: PosControllerService,
    private shiftService: ShiftControllerService,
    private physicalShopService: PhysicalShopControllerService
  ) {}

  /**
   * Process API response that might be a Blob or JSON
   */
  private processApiResponse<T>(response: any): Observable<T> {
    if (response instanceof Blob) {
      return from(
        new Promise<T>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const textData = reader.result as string;
              
              // Try to parse as JSON first
              try {
                const jsonData = JSON.parse(textData);
                // Check if the response contains an error
                if (this.isErrorResponse(jsonData)) {
                  reject(new Error(jsonData.message || 'Unknown error occurred'));
                } else {
                  resolve(jsonData as T);
                }
              } catch (jsonError) {
                // If JSON parsing fails, check if it's a plain text response
                if (textData.trim().length > 0) {
                  // For plain text responses, return a success object
                  resolve({
                    message: textData.trim(),
                    success: true,
                    isPlainText: true
                  } as T);
                } else {
                  // Empty response
                  resolve({
                    message: 'Empty response',
                    success: true,
                    isPlainText: true
                  } as T);
                }
              }
            } catch (error) {
              console.error('Error processing blob data:', error);
              reject(error);
            }
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsText(response);
        })
      );
    }
    
    // Check if the response contains an error
    if (this.isErrorResponse(response)) {
      throw new Error(response.message || 'Unknown error occurred');
    }
    
    return of(response as T);
  }

  /**
   * Check if the response contains an error
   */
  private isErrorResponse(response: any): boolean {
    // Check for common error response patterns
    if (response && typeof response === 'object') {
      // Pattern 1: {code: 500, message: 'Error message'}
      if (response.code && (response.code >= 400 || response.code === 500)) {
        return true;
      }
      // Pattern 2: {error: 'Error message'}
      if (response.error) {
        return true;
      }
      // Pattern 3: {status: 'error', message: 'Error message'}
      if (response.status === 'error') {
        return true;
      }
    }
    return false;
  }

  /**
   * Get physical shops for search dropdown (all shops)
   */
  getPhysicalShopsForSearch(): Observable<ShopOption[]> {
    const searchDTO: PhysicalShopShowDTO = {
      status: undefined,
      deleteFlag: false,
      pageRequest: {
        page: 0,
        size: 20
      }
    };

    return this.physicalShopService.showPage(searchDTO, 'body', false).pipe(
      switchMap((response) => this.processApiResponse<PageInfoPhysicalShopVO>(response)),
      map((response: PageInfoPhysicalShopVO) => {
        const shops = response.pageContent || [];
        return shops.map(shop => ({
          label: shop.name || '',
          value: shop.id || 0
        }));
      }),
      catchError((error) => {
        console.error('Error fetching physical shops for search:', error);
        throw error;
      })
    );
  }

  /**
   * Get shop details by ID
   */
  getShopDetailsById(shopId: number): Observable<PhysicalShopDetailVO | null> {
    return this.physicalShopService.showDetails(shopId, 'body', false).pipe(
      switchMap((response) => this.processApiResponse<PhysicalShopDetailVO>(response)),
      map((response: PhysicalShopDetailVO) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error fetching shop details:', error);
        throw error;
      })
    );
  }

  /**
   * Get POS shift opening records with search parameters
   * Only returns shifts with status 0 (create) and 1 (open)
   */
  getPosShiftOpenings(params: PosShiftOpeningSearchParams = {}): Observable<PosShiftOpeningListResponse> {
    const page = params.page || 0; // Use 0-based pagination
    const size = params.size || 20;
    
    // Convert date strings to ISO 8601 format for Java Instant
    const startTimeFrom = params.startTimeFrom ? convertToInstant(params.startTimeFrom) : undefined;
    const startTimeTo = params.startTimeTo ? convertToInstant(params.startTimeTo) : undefined;
    
    // Filter for status 0 (create) and 1 (open) only
    const statusFilter = params.status === undefined ? [0, 1] : [params.status];
    return this.shiftService.getAllShifts(
      undefined, // userId
      params.cashierName, // name (cashier name)
      undefined, // email
      undefined, // startTotalCash
      startTimeFrom, // startTimeFrom
      startTimeTo, // startTimeTo
      params.posId, // posId
      statusFilter, // status array - only get status 0 and 1
      page,
      size,
      'startTime', // sortBy - sort by creation date
      false // ascending - false for descending order (latest first)
    ).pipe(
      switchMap((response) => this.processApiResponse<PageInfoShiftsResDTO>(response)),
      map((response: PageInfoShiftsResDTO) => {
        const shifts = response.pageContent || [];
        const shiftOpenings: PosShiftOpening[] = shifts.map(shift => this.mapShiftToPosShiftOpening(shift));
        
        return {
          data: shiftOpenings,
          totalRecords: response.totalElements || 0,
          loading: false
        };
      }),
      catchError((error) => {
        console.error('Error fetching POS shift openings:', error);
        throw error;
      })
    );
  }

  /**
   * Map ShiftsResDTO to PosShiftOpening
   */
  private mapShiftToPosShiftOpening(shift: ShiftsResDTO): PosShiftOpening {
    // Extract POS data from the shift.pos object
    const posData = shift.pos;

    return {
      id: shift.id || 0,
      shift_id: `POS-OPENING-${shift.id}`,
      posId: shift.posId || 0,
      posCode: posData?.code || shift.posId?.toString() || '',
      shopId: posData?.channelShopId || 0,
      shopName: posData?.channelShopName || '',
      shopAddress: posData?.channelShopAddress || '',
      cashierId: 0, // Will need to be mapped from userId
      cashierName: shift.name || '',
      shiftDate: shift.startTime || '',
      openingTime: shift.startTime || '',
      closingTime: shift.endTime || '',
      openingAmount: shift.startTotalCash || 0,
      closingAmount: shift.endTotalCash,
      status: this.mapShiftStatus(shift.status),
      createdBy: shift.createdBy || '',
      creationDate: shift.creationDate || '',
      lastUpdatedBy: shift.lastUpdatedBy,
      lastUpdateDate: shift.lastUpdateDate
    };
  }

  /**
   * Map API status to our status enum
   */
  private mapShiftStatus(apiStatus?: number): 'OPEN' | 'CLOSED' | 'CREATE' {
    if (!apiStatus) return 'CREATE';
    
    switch (apiStatus) {
      case 0:
        return 'CREATE';
      case 1:
        return 'OPEN';
      case 2:
        return 'CLOSED';
      default:
        return 'CREATE';
    }
  }

  /**
   * Get POS shift opening by ID
   */
  getPosShiftOpeningById(id: number): Observable<PosShiftOpening | null> {
    return this.shiftService.getShiftById(id).pipe(
      switchMap((response) => this.processApiResponse<ShiftsResDTO>(response)),
      map((response: ShiftsResDTO) => {
        return this.mapShiftToPosShiftOpening(response);
      }),
      catchError((error) => {
        console.error('Error fetching shift opening by ID:', error);
        throw error;
      })
    );
  }

  /**
   * Get POS terminal options for dropdown
   */
  getPosTerminalOptionsPage(params: {
    page: number;
    size: number;
    searchText: string;
  }): Observable<{
    options: ShopOption[];
    pageContentLength: number;
    totalElements?: number;
  }> {
    const searchText = params.searchText?.trim();
    return this.posService
      .getAllPosPage({
        page: params.page - 1,
        size: params.size,
        code: searchText || undefined,
      })
      .pipe(
        switchMap((response) =>
          this.processApiResponse<PageInfoPosResDTO>(response),
        ),
        map((response: PageInfoPosResDTO) => {
          const posTerminals = response.pageContent || [];
          const options = posTerminals.map((pos) => ({
            label: `${pos.code} (${pos.channelShopName || 'Unknown Shop'})`,
            value: pos.id || 0,
          }));
          return {
            options,
            pageContentLength: options.length,
            totalElements: response.totalElements,
          };
        }),
        catchError(() => of({ options: [], pageContentLength: 0 })),
      );
  }

  getPosTerminalOptions(): Observable<ShopOption[]> {
    const pageRequest = {
      page: 0,
      size: 20,
    };

    return this.posService.getAllPosPage(pageRequest).pipe(
      switchMap((response) => this.processApiResponse<PageInfoPosResDTO>(response)),
      map((response: PageInfoPosResDTO) => {
        const posTerminals = response.pageContent || [];
        return posTerminals.map(pos => ({
          label: `${pos.code} (${pos.channelShopName || 'Unknown Shop'})`,
          value: pos.id || 0
        }));
      }),
      catchError((error) => {
        console.error('Error fetching POS terminal options:', error);
        // Return empty array on error to prevent breaking the UI
        return of([]);
      })
    );
  }
}

